<template>
  <div class="month-table" data-testid="month-table">
    <div v-if="!day">
      <div class="table-header-row">
        <h3 class="h4 card-secondary-header">{{ monthString }}</h3>
        <div></div>
        <div></div>
      </div>
      <ag-grid-vue
        class="ag-theme-alpine"
        :columnDefs="columnDefs"
        :rowData="rowData"
        :groupDisplayType="'groupRows'"
        :groupDefaultExpanded="1"
        :groupRowRendererParams="groupRowRendererParams"
        :suppressColumnVirtualisation="true"
        :defaultColDef="defaultColDef"
        @grid-ready="onGridReady"
        @newColumnsLoaded="newColumnsLoaded"
      >
      </ag-grid-vue>
      <div class="table-warning" v-if="shouldShowTableWarning">
        <div>
          <i class="far fa-info-circle"></i> Workload data available from
          {{ tableWarningDate }}
        </div>
      </div>
    </div>
    <DayTable
      v-if="day"
      :day="day"
      :unitOfWork="unitOfWork"
      :timezone="timezone"
      :allowPrevious="allowPrevious"
      :allowNext="allowNext"
      :workloadTasks="workloadTasks"
      :configurationId="configurationId"
      @back="previousDay"
      @forward="nextDay"
      @close="closeDailyTable"
    />
  </div>
</template>

<script>
import "ag-grid-enterprise";
import { AgGridVue } from "ag-grid-vue";
import { Translator } from "@agyletime/wfm-vue-common";
import DateHeader from "../DateHeader.vue";
import DayTable from "./DayTable.vue";
import { adjustAvailableForTask } from "../../../utils/intervalUtils";
import { roundToNearestMonth } from "@agyletime/date-utils";
import { utcToZonedTime } from "date-fns-tz";

export default {
  name: "MonthTable",
  mixins: [Translator],
  props: {
    configurationId: {
      type: String,
      required: true,
    },
    timezone: {
      type: String,
      required: true,
    },
    monthData: {
      type: Array,
      required: true,
    },
    month: {
      type: Date,
      required: true,
    },
    unitOfWork: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    workloadTasks: {
      type: Map,
      required: true,
    },
  },
  watch: {
    month() {
      this.closeDailyTable();
    },
    monthData() {
      this.setTableData();
    },
    loading() {
      this.handleLoading();
    },
  },
  created() {
    this.setTableData();
  },
  data() {
    return {
      columnDefs: null,
      rowData: null,
      firstDateWithWorkloads: null,
      day: null,
      dayIndex: null,
    };
  },
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    agColumnHeader: DateHeader,
    DayTable,
  },
  methods: {
    featureEnabled(key) {
      return window.PlayvoxWFM.featureflow.evaluate(key).isOn();
    },
    handleLoading() {
      if (this.gridApi) {
        if (this.loading || this.monthData.length === 0) {
          this.gridApi.showLoadingOverlay();
        } else {
          this.gridApi.hideOverlay();
        }
      }
    },
    setTableData() {
      this.firstDateWithWorkloads = this.monthData.find(
        (day) => day.workloadTasks && day.workloadTasks.length > 0
      );

      this.columnDefs = [
        {
          field: "task.name",
          rowGroup: true,
          cellRenderer: "agGroupCellRenderer",
          hide: true,
          columnGroupShow: "open",
        },
        {
          headerName: this.translate("workload-task-name"),
          field: "type",
          menuTabs: this.menuTabs,
          width: 130,
          flex: 0,
          resizable: false,
          suppressSizeToFit: true,
          pinned: "left",
          type: "rightAligned",
        },
        // One column for every day of the month
        ...this.monthData.map((day, index) => {
          return {
            headerName: this.getValueInLocale(
              utcToZonedTime(day.date, this.timezone),
              {
                day: "2-digit",
              }
            ),
            valueGetter: (params) => {
              if (!params.data) {
                return "";
              }
              return params.data?.days[index];
            },
            menuTabs: this.menuTabs,
            type: "numericColumn",
            flex: 1,
            click: () => {
              this.setDay(day);
              this.dayIndex = index;
            },
            isDate: true,
          };
        }),
      ];

      this.rowData = this.getRowData();
      this.sizeToFit();
      this.handleLoading();
    },
    getRowData() {
      const workloadsForAllDay = this.monthData
        .flatMap((day) => day.workloadTasks)
        .map((task) => task?.task)
        .filter(Boolean);

      const uniqueWorkloadsById = workloadsForAllDay.reduce((acc, task) => {
        acc.set(task.id, task);
        return acc;
      }, new Map());

      let uniqueWorkloads = Array.from(uniqueWorkloadsById.values());

      // sort by name prop
      uniqueWorkloads.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

      // Map unique workloads to their daily values
      return uniqueWorkloads.flatMap((workload) => {
        const workloads = [
          {
            task: workload,
            type: this.translate("monthly-summary-available"),
            days: this.getMonthlyProp(workload, "available", (task) =>
              adjustAvailableForTask({
                task,
                unitOfWork: this.unitOfWorkProp,
              }).toFixed(1)
            ),
          },
          {
            task: workload,
            type: this.translate("monthly-summary-staffing"),
            days: this.getMonthlyProp(
              workload,
              "available",
              (task) =>
                adjustAvailableForTask({
                  task,
                  unitOfWork: this.unitOfWorkProp,
                }).toFixed(1),
              true
            ),
          },
          {
            task: workload,
            type: this.translate("monthly-summary-required"),
            days: this.getMonthlyProp(workload, "required"),
          },
          {
            task: workload,
            type: this.translate("monthly-summary-difference"),
            days: this.getMonthlyProp(workload, "difference", (task, index) => {
              const required = task?.required?.[this.unitOfWorkProp] || 0;
              const availableAdjusted = adjustAvailableForTask({
                task,
                unitOfWork: this.unitOfWorkProp,
              });

              const staffingTask = this.monthData[index]?.workloadTasks?.find(
                (t) => t.workloadTaskId === workload.id && t.isAdjustment
              );

              const staffing = staffingTask
                ? adjustAvailableForTask({
                    task: staffingTask,
                    unitOfWork: this.unitOfWorkProp,
                  })
                : 0;

              if (availableAdjusted === 0 && required === 0 && staffing === 0) {
                return 0;
              }

              return (availableAdjusted + staffing - required).toFixed(1);
            }),
          },
        ];

        if (this.featureEnabled("capacity-planner-forecast-sla")) {
          workloads.push({
            task: workload,
            type: this.translate("monthly-summary-forecast"),
            days: this.getMonthlyProp(workload, "forecast", (task) => {
              if (typeof task?.sla === "undefined") return "-";
              return ((task.sla || 0) * 100).toFixed(0);
            }),
          });
        }

        return workloads;
      });
    },
    getMonthlyProp(workload, prop, calc, isAdjustment = false) {
      return this.monthData.map((day, index) => {
        const workloadTask = day.workloadTasks?.find(
          (task) =>
            task.task.id === workload.id && task.isAdjustment === isAdjustment
        );

        return calc
          ? calc(workloadTask, index)
          : this.getTaskValueOrDash(day, workloadTask, prop);
      });
    },
    getTaskValueOrDash(day, task, prop) {
      if (
        !task &&
        (!this.firstDateWithWorkloads ||
          this.firstDateWithWorkloads.date > day.date)
      ) {
        return "-";
      }

      const value = task?.[prop]?.[this.unitOfWorkProp] || 0;
      return value ? value.toFixed(1) : 0;
    },
    sizeToFit() {
      this.gridColumnApi?.autoSizeAllColumns();
    },
    newColumnsLoaded() {
      this.sizeToFit();
      this.handleLoading();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    },
    setDay(day) {
      this.day = day;
      this.$emit("day-selected", day);
    },
    previousDay() {
      this.dayIndex--;
      this.setDay(this.monthData[this.dayIndex]);
    },
    nextDay() {
      this.dayIndex++;
      this.setDay(this.monthData[this.dayIndex]);
    },
    closeDailyTable() {
      this.setDay(null);
      this.dayIndex = null;
    },
  },
  computed: {
    unitOfWorkProp() {
      return this.unitOfWork === "FTE" ? "fte" : "hours";
    },
    valueOrZero() {
      const prop = this.unitOfWorkProp;
      return (value) => {
        if (value[prop]) {
          return value[prop];
        } else {
          return 0;
        }
      };
    },
    defaultColDef() {
      return { flex: 1, minWidth: 60, resizable: true };
    },
    menuTabs() {
      return [];
    },
    monthString() {
      return this.getValueInLocale(roundToNearestMonth({ date: this.month }), {
        month: "long",
      });
    },
    allowPrevious() {
      return this.dayIndex > 0;
    },
    allowNext() {
      return this.dayIndex < this.monthData.length - 1;
    },
    groupRowRendererParams() {
      return {
        suppressCount: true,
        suppressDoubleClickExpand: true,
        suppressEnterExpand: true,
      };
    },
    shouldShowTableWarning() {
      return (
        !this.firstDateWithWorkloads ||
        this.firstDateWithWorkloads.date.getDate() > 1
      );
    },
    tableWarningDate() {
      return this.getValueInLocale(
        this.firstDateWithWorkloads?.date || new Date(),
        {
          day: "2-digit",
          month: "2-digit",
        }
      );
    },
  },
};
</script>

<style lang="scss">
@import "ag-grid-community/dist/styles/ag-grid.css";
@import "ag-grid-community/dist/styles/ag-theme-alpine.css";

.month-table .ag-theme-alpine {
  height: 400px;
}

.table-header-row {
  margin-bottom: 1rem;

  display: flex;

  & > * {
    flex: 1;
  }
}

/* Hide the collapse chevron */
.ag-root-wrapper .ag-row > .ag-row-group > .ag-group-expanded {
  display: none;
}

.table-warning {
  margin-top: 1rem;
}
</style>
